import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getRouteApi, type RouteIds, useRouter } from '@tanstack/react-router';
import { Skeleton } from '@ui/skeleton.tsx';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { FormattedHtml } from '@r/components/formatted-html.tsx';
import {
  BreadcrumbNavigation,
  type BreadcrumbItemType
} from '@client/components/breadcrumb/breadcrumb-nav.tsx';
import { useApiGamesIdGet } from '@r/api/generated/client.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { PageContainer } from '@client/components/layout/page-container.tsx';

const GameDetail = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { id } = getRouteApi('/games/$id' as RouteIds<typeof router.routeTree>).useParams() as {
    id: string;
  };

  const user = useUser().data;
  const workspaces = UseWorkspace(user);
  const isDataAvailable = Boolean(id && user?.id && workspaces.workspace?.id);

  const gameQuery = useApiGamesIdGet(id, {
    query: {
      queryKey: ['game', id, workspaces.workspace?.id, user?.id],
      enabled: isDataAvailable
    }
  });
  useOrvalQueryErrorToast(gameQuery, t('toast.error.label'));

  const { data: game, isPending, isError, isSuccess } = gameQuery;

  const iframeUrl = useMemo(() => {
    if (!game || !user) {
      return '';
    }

    const activeUserWorkspace = getActiveWorkspaceUniversal(user.visibleWorkspaces);
    const url = new URL(game.link, window.location.origin);
    const params: Record<string, string | undefined> = {
      customer_number: activeUserWorkspace?.uniqueCode,
      company_unique_code: activeUserWorkspace?.company?.uniqueCode,
      workspace_id: activeUserWorkspace?.workspace?.id?.toString()
    };
    for (const [key, value] of Object.entries(params)) {
      if (value) {
        url.searchParams.set(key, value);
      }
    }

    return url.toString();
  }, [game, user]);

  const breadcrumbItems: BreadcrumbItemType[] = useMemo(() => {
    if (!game) {
      return [{ label: t('games.title'), to: '/games' }];
    }

    return [
      { label: t('games.title'), to: '/games' },
      { label: game.label, isCurrentPage: true }
    ];
  }, [game, t]);

  return (
    <PageContainer className="flex flex-col gap-6 pt-10 pb-7">
      <header className="space-y-5">
        <BreadcrumbNavigation items={breadcrumbItems} />

        <div className="space-y-3">
          <h1 className="text-2xl font-bold text-foreground md:text-3xl lg:text-[2.75rem] lg:leading-[3.5rem]">
            {t('games.detail.title')}
          </h1>

          {isSuccess && (
            <FormattedHtml
              as="div"
              text={game?.description}
              className="text-sm text-foreground/80"
            />
          )}
        </div>
      </header>

      {isPending && <Skeleton className="h-[32rem] w-full rounded-3xl" />}

      {isError && (
        <div className="p-6 rounded-3xl border border-destructive/40 bg-destructive/5 text-destructive">
          {t('games.detail.error')}
        </div>
      )}

      {isSuccess && game && (
        <iframe
          src={iframeUrl}
          title={game.label}
          className="w-full border-0 h-[1136px]"
          loading="lazy"
          allowFullScreen
        />
      )}
    </PageContainer>
  );
};

export { GameDetail };
